import { useEffect, useMemo, useRef, useState } from "react";
import {
	Alert,
	Autocomplete,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	LinearProgress,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import LinkOutlined from "@mui/icons-material/LinkOutlined";
import { ReferenceName } from "./ReferenceNames";
import type {
	CatalogOption,
	CatalogReference,
	ReferenceLinkChange,
} from "./models";
import {
	ComparisonRequestError,
	getReferenceOptions,
	saveReferenceLinks,
} from "./repository";

export function ReferenceLinksDialog({
	items,
	snapshotToken,
	onClose,
	onInvalidate,
}: {
	items: CatalogReference[];
	snapshotToken: string;
	onClose: () => void;
	onInvalidate: (message: string, success: boolean) => void;
}) {
	const [options, setOptions] = useState<CatalogOption[]>([]);
	const [choices, setChoices] = useState<Map<string, CatalogOption | null>>(
		() => new Map(),
	);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [stale, setStale] = useState(false);
	const [error, setError] = useState("");
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(0);
	const active = useRef<AbortController | null>(null);
	const concept = items[0].concept;
	const catalogName = items[0].catalogName;
	const byId = useMemo(
		() => new Map(options.map((option) => [option.id, option])),
		[options],
	);
	const pendingItems = items.filter((item) => !item.targetName);
	const changes: ReferenceLinkChange[] = pendingItems.flatMap((item) => {
		const choice = choices.get(item.name);
		return choice && choice.id !== item.targetId
			? [
					{
						name: item.name,
						targetId: choice.id,
						expectedTargetId: item.targetId,
					},
				]
			: [];
	});
	const filtered = pendingItems.filter((item) =>
		`${item.name} ${item.targetName ?? ""} ${item.usages.map((usage) => `${usage.catalogName} ${usage.location}`).join(" ")}`
			.toLowerCase()
			.includes(search.toLowerCase()),
	);
	useEffect(() => {
		const controller = new AbortController();
		getReferenceOptions(concept, controller.signal)
			.then((values) => {
				if (!controller.signal.aborted) setOptions(values);
			})
			.catch((reason) => {
				if (!controller.signal.aborted)
					setError(
						reason instanceof Error
							? reason.message
							: "Could not load MES records.",
					);
			})
			.finally(() => {
				if (!controller.signal.aborted) setLoading(false);
			});
		return () => controller.abort();
	}, [concept]);
	useEffect(() => () => active.current?.abort(), []);
	async function save() {
		if (active.current || loading || stale || changes.length === 0) return;
		const controller = new AbortController();
		active.current = controller;
		setSaving(true);
		setError("");
		try {
			const result = await saveReferenceLinks(
				snapshotToken,
				changes,
				controller.signal,
			);
			if (controller.signal.aborted) return;
			onInvalidate(`${result.savedLinks} links saved in ${catalogName}.`, true);
			onClose();
		} catch (reason) {
			if (controller.signal.aborted) return;
			const message =
				reason instanceof Error
					? reason.message
					: "The result could not be confirmed. Refresh this category.";
			setError(message);
			if (!(reason instanceof ComparisonRequestError) || reason.requiresRerun) {
				setStale(true);
				onInvalidate(message, false);
			}
		} finally {
			active.current = null;
			if (!controller.signal.aborted) setSaving(false);
		}
	}
	return (
		<Dialog
			open
			fullWidth
			maxWidth="lg"
			onClose={saving ? undefined : onClose}
			disableEscapeKeyDown={saving}
		>
			<DialogTitle>Link to existing MES items — {catalogName}</DialogTitle>
			<DialogContent>
				<Stack gap={2}>
					{error && <Alert severity="error">{error}</Alert>}
					<Typography variant="body2">
						Choose a MES record for each pending name. Blank selections are
						skipped. All selected links are saved together.
					</Typography>
					<TextField
						size="small"
						label="Search referenced names"
						value={search}
						disabled={saving}
						onChange={(event) => {
							setSearch(event.target.value);
							setPage(0);
						}}
					/>
					{(loading || saving) && (
						<LinearProgress
							aria-label={saving ? "Saving links" : "Loading MES records"}
						/>
					)}
					<TableContainer sx={{ maxHeight: 480 }} key={`${page}:${search}`}>
						<Table
							size="small"
							stickyHeader
							aria-label="Reference links to save"
						>
							<TableHead>
								<TableRow>
									<TableCell>Rollshop name</TableCell>
									<TableCell>Existing MES item</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{filtered.slice(page * 25, page * 25 + 25).map((item) => (
									<TableRow key={item.name}>
										<TableCell sx={{ width: "45%" }}>
											<ReferenceName name={item.name} />
											<Typography
												variant="caption"
												color="text.secondary"
												display="block"
											>
												Used in:{" "}
												{item.usages
													.map(
														(usage) =>
															`${usage.catalogName} — ${usage.location} (${usage.count})`,
													)
													.join(", ")}
											</Typography>
											{item.targetName && (
												<Typography
													variant="caption"
													color="success.main"
													display="block"
												>
													{item.targetId ? "Current link" : "MES name match"}:{" "}
													{item.targetName}
												</Typography>
											)}
										</TableCell>
										<TableCell>
											<Autocomplete
												size="small"
												options={options}
												loading={loading}
												disabled={loading || saving || stale}
												value={
													choices.get(item.name) === undefined
														? (byId.get(item.targetId ?? "") ?? null)
														: choices.get(item.name)
												}
												getOptionLabel={(option) =>
													`${option.name} (ID ${option.id})`
												}
												isOptionEqualToValue={(a, b) => a.id === b.id}
												onChange={(_, value) =>
													setChoices((current) =>
														new Map(current).set(item.name, value),
													)
												}
												renderInput={(params) => (
													<TextField
														{...params}
														label={`MES item for ${item.name}`}
													/>
												)}
											/>
										</TableCell>
									</TableRow>
								))}
								{filtered.length === 0 && (
									<TableRow>
										<TableCell colSpan={2}>
											{pendingItems.length === 0
												? "No pending referenced names."
												: "No matching referenced names."}
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
					<TablePagination
						component="div"
						count={filtered.length}
						page={page}
						rowsPerPage={25}
						rowsPerPageOptions={[25]}
						onPageChange={(_, value) => setPage(value)}
					/>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Typography variant="body2" sx={{ mr: "auto", ml: 2 }}>
					{changes.length} links selected
				</Typography>
				<Button variant="outlined" disabled={saving} onClick={onClose}>
					Cancel
				</Button>
				<Button
					variant="contained"
					startIcon={<LinkOutlined />}
					disabled={loading || saving || stale || changes.length === 0}
					onClick={save}
				>
					Save links ({changes.length})
				</Button>
			</DialogActions>
		</Dialog>
	);
}
